// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ActiveClaimSession } from '@/services/claimSession';
import { getUsableJwt } from '@/services/jwt';

/**
 * The subset of an {@link ActiveClaimSession} that actually carries access credentials. An
 * invite viewer has no claim session at all — only an invite token from the URL — so this lets
 * `{ inviteToken }` be passed where a full session would otherwise be required.
 */
export type AccessCredentials = Partial<
  Pick<ActiveClaimSession, 'claimAccessToken' | 'claimClientId' | 'inviteToken' | 'startedRoundId'>
>;

export const hasClaimAccess = (claimSession: AccessCredentials | null | undefined): boolean =>
  !!getUsableJwt(claimSession?.claimAccessToken);

export const appendClaimQuery = (
  url: string,
  claimSession: AccessCredentials | null | undefined,
): string => {
  if (!claimSession) {
    return url;
  }

  let claimToken = getUsableJwt(claimSession.claimAccessToken);
  const claimClientId = claimSession.claimClientId?.trim();
  const inviteToken = claimSession.inviteToken?.trim();
  const startedRoundId = claimSession.startedRoundId?.trim().toLowerCase() ?? null;

  if (claimToken && startedRoundId && isRoundScopedRequestForDifferentRound(url, startedRoundId)) {
    claimToken = null;
  }

  if (!claimToken && !claimClientId && !inviteToken) {
    return url;
  }

  const params = new URLSearchParams();
  if (claimToken) {
    params.set('claimToken', claimToken);
  }
  if (claimClientId) {
    params.set('claimClientId', claimClientId);
  }
  if (inviteToken) {
    params.set('inviteToken', inviteToken);
  }

  const query = params.toString();
  if (!query) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
};

const isRoundScopedRequestForDifferentRound = (url: string, startedRoundId: string): boolean => {
  const path = extractPathname(url);
  if (!path) {
    return false;
  }

  const match = path.match(/\/api\/rounds\/([^/]+)/i);
  if (!match) {
    return false;
  }

  const scope = (match[1] ?? '').trim().toLowerCase();
  if (!scope || scope === 'tour' || scope === 'latest-per-player' || scope === 'players') {
    return false;
  }

  return scope !== startedRoundId;
};

const extractPathname = (url: string): string | null => {
  try {
    return new URL(url).pathname;
  } catch {
    const [pathOnly] = url.split('?');
    return pathOnly || null;
  }
};

export const buildAccessHeaders = (userToken?: string | null): HeadersInit | undefined => {
  const normalized = userToken?.trim();
  if (!normalized) {
    return undefined;
  }

  return {
    Authorization: `Bearer ${normalized}`,
  };
};
